import {
  InfUpdateData,
  UpdateInferencesSections,
} from '@/api/manualCalculation/types';
import {
  AvmInferenceData,
  InferenceScoresData,
  VarianceAnalysisData,
} from '@/api/workOrders/types';

import {
  EditableSections,
  ComplementaryInfoSection,
  EvaluationValuesSection,
  HypothesisSection,
  OutliersSection,
  StatisticsSection,
  VariableDescriptionSection,
} from '../types';

const DefaultVarianceData: VarianceAnalysisData = {
  source: '',
  sum_of_squares: 0,
  degrees_of_freedom: 0,
  squares_mean: 0,
  f: 0,
};

export const extractEditableSections = (
  data: AvmInferenceData
): EditableSections => {
  return {
    evaluationValues: {
      estimated_value_mean: data.estimated_value_mean,
      estimated_value_lower: data.estimated_value_lower,
      estimated_value_upper: data.estimated_value_upper,
      estimated_lower_pct: data.estimated_lower_pct,
      estimated_upper_pct: data.estimated_upper_pct,
      mean_total_value_estimate: data.mean_total_value_estimate,
      min_total_value_estimate: data.min_total_value_estimate,
      max_total_value_estimate: data.max_total_value_estimate,
      rounded_market_value: data.rounded_market_value,
      report_date: data.report_date,
    } as EvaluationValuesSection,

    scores: data.scores as InferenceScoresData,

    statistics: {
      rsquared: data.rsquared,
      r: data.r,
      f: data.f,
      model_significance_pct: data.model_significance_pct,
    } as StatisticsSection,

    residuals:
      data.residuals_distribution?.length >= 3
        ? data.residuals_distribution
        : [0, 0, 0],

    residualsCurve:
      data.residuals_distribution_normal_curve &&
      data.residuals_distribution_normal_curve.length >= 3
        ? data.residuals_distribution_normal_curve
        : [68, 90, 95],

    complementaryInfo: {
      all_data_count: data.all_data_count,
      model_data_count: data.model_data_count,
      total_variables: data.total_variables,
      model_total_variables: data.model_total_variables,
    } as ComplementaryInfoSection,

    outliers: {
      outliers_quantity: data.outliers_quantity ?? 0,
      outliers_percentage: data.outliers_percentage ?? 0,
    } as OutliersSection,

    variance:
      Array.isArray(data.variance_analysis) &&
      data.variance_analysis.length >= 3
        ? data.variance_analysis
        : [
            { ...DefaultVarianceData, source: 'Explicada' },
            { ...DefaultVarianceData, source: 'Não Explicada' },
            { ...DefaultVarianceData, source: 'Total' },
          ],

    variableDescriptions: data.variable_descriptions
      ? (data.variable_descriptions as VariableDescriptionSection)
      : {
          dependent_variable: { name: '', description: '', unit: '' },
          independent_variables: [],
        },

    hypothesis: (() => {
      const independentCount = data.variable_descriptions
        ? (data.variable_descriptions as VariableDescriptionSection)
            .independent_variables.length
        : 0;
      const totalCount = 1 + independentCount;
      const existingTests = Array.isArray(data.hypothesis_tests)
        ? data.hypothesis_tests
        : [];
      const hypothesisTests = Array.from(
        { length: totalCount },
        (_, i) =>
          existingTests[i] ?? {
            variable: '',
            transformation: '',
            t_obs: 0,
            significance: 0,
          }
      );
      return { hypothesis_tests: hypothesisTests } as HypothesisSection;
    })(),

    equation: data.equation,

    correlation: data.corr_matrix,
  };
};

export function buildSectionPayload(
  section: UpdateInferencesSections,
  data: EditableSections
): InfUpdateData {
  const {
    evaluationValues,
    scores,
    statistics,
    residuals,
    residualsCurve,
    complementaryInfo,
    outliers,
    variance,
    hypothesis,
    variableDescriptions,
    equation,
    correlation,
  } = data;

  switch (section) {
    case 'result':
      return {
        estimated_value_mean: evaluationValues.estimated_value_mean,
        estimated_value_lower: evaluationValues.estimated_value_lower,
        estimated_value_upper: evaluationValues.estimated_value_upper,
        estimated_lower_pct: evaluationValues.estimated_lower_pct,
        estimated_upper_pct: evaluationValues.estimated_upper_pct,
        mean_total_value_estimate: evaluationValues.mean_total_value_estimate,
        min_total_value_estimate: evaluationValues.min_total_value_estimate,
        max_total_value_estimate: evaluationValues.max_total_value_estimate,
        rounded_market_value: evaluationValues.rounded_market_value,
      };
    case 'statistics':
      return {
        r: statistics.r,
        rsquared: statistics.rsquared,
        f: statistics.f,
        model_significance_pct: statistics.model_significance_pct,
        residuals_distribution: residuals,
        residuals_distribution_normal_curve: residualsCurve,
        total_variables: complementaryInfo.total_variables,
        model_total_variables: complementaryInfo.model_total_variables,
        all_data_count: complementaryInfo.all_data_count,
        model_data_count: complementaryInfo.model_data_count,
        outliers_quantity: outliers.outliers_quantity,
        outliers_percentage: outliers.outliers_percentage,
        variance_analysis: variance,
        hypothesis_tests: (() => {
          const variableNames = [
            variableDescriptions.dependent_variable.name,
            ...variableDescriptions.independent_variables.map((v) => v.name),
          ];
          return hypothesis.hypothesis_tests.map((test, i) => ({
            ...test,
            variable: variableNames[i] ?? test.variable,
          }));
        })(),
        variable_descriptions: variableDescriptions,
        equation,
        corr_matrix: correlation,
      };
    default:
      return { scores };
  }
}
